import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Loader2, LogOut, Lock, ArrowLeft, Eye, EyeOff, X,
  LayoutGrid, Users, SquarePlus, FileText, CheckCircle2, Receipt,
  FileBarChart, BookOpen, Calculator, Inbox, Settings, ShieldCheck,
} from "lucide-react";
import { TeamSetup } from "@/components/TeamSetup";
import { AdminDashboard } from "@/components/AdminDashboard";
import { AdminCustomers } from "@/components/AdminCustomers";
import { AdminRates } from "@/components/AdminRates";
import { AdminQuoteNew } from "@/components/AdminQuoteNew";
import { AdminQuotations } from "@/components/AdminQuotations";
import { AdminOrders } from "@/components/AdminOrders";
import { AdminBilling } from "@/components/AdminBilling";
import { AdminStatements } from "@/components/AdminStatements";
import { AdminLedger } from "@/components/AdminLedger";
import { AdminCalculator } from "@/components/AdminCalculator";
import { AdminEnquiries } from "@/components/AdminEnquiries";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const client = axios.create({ withCredentials: true });

const TABS = [
  { id: "dash", label: "Dashboard", icon: LayoutGrid, perm: "dashboard:view", eyebrow: "Admin", title: "Dashboard", sub: "Click any tile to drill down." },
  { id: "customers", label: "Customers", icon: Users, perm: "customers:view", eyebrow: "Customer", title: "Customers", sub: "Your customer directory — add once, quote fast." },
  { id: "new-quote", label: "New Quote", icon: SquarePlus, perm: "quotations:create", eyebrow: "Sales", title: "New Quotation", sub: "Build a quotation line by line with live pricing." },
  { id: "quotes", label: "Quotations", icon: FileText, perm: "quotations:view", eyebrow: "Sales", title: "Quotations", sub: "Every quotation you have raised — print, email or revise." },
  { id: "orders", label: "Orders", icon: CheckCircle2, perm: "orders:view", eyebrow: "Confirmed", title: "Orders", sub: "Quotations that customers have accepted / ordered." },
  { id: "billing", label: "Billing", icon: Receipt, perm: "bills:view", eyebrow: "Ledger", title: "Sales & Purchase Billing", sub: "Record every bill you raise (sales) and every bill you receive (purchase)." },
  { id: "statements", label: "Statements", icon: FileBarChart, perm: "statements:view", eyebrow: "Customer", title: "Monthly Statement", sub: "Confirmed orders and sale bills for a customer, month-wise." },
  { id: "ledger", label: "Ledger", icon: BookOpen, perm: "ledger:view", eyebrow: "Customer", title: "Ledger (all-time)", sub: "Every quotation, order, bill and payment for a customer." },
  { id: "calculator", label: "Calculator", icon: Calculator, perm: "calculator:view", eyebrow: "Tools", title: "Quick Calculator", sub: "Instant price for any single tray — nothing is saved. Uses your current Rate Settings." },
  { id: "enquiries", label: "Enquiries", icon: Inbox, perm: "enquiries:view", eyebrow: "Website", title: "Enquiries", sub: "Quote requests submitted from your website contact form." },
  { id: "rates", label: "Rates", icon: Settings, perm: "rates:view", eyebrow: "Admin", title: "Rate Settings", sub: "Sheet rate, GST, overhead and margin — drives every quote and bill." },
  { id: "team", label: "Team", icon: ShieldCheck, perm: "team:view", eyebrow: "Admin Panel", title: "Team & Permissions", sub: "Create custom teams with tab-wise permission checklists, then assign each employee to a team." },
];

const formatDetail = (detail) => {
  if (detail == null) return "Login failed. Check your credentials.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => e?.msg || JSON.stringify(e)).join(" ");
  return String(detail?.msg || detail);
};

export default function Admin() {
  const [user, setUser] = useState(null); // null = checking, false = logged out
  const [tab, setTab] = useState(null);
  const [form, setForm] = useState({ email: "", password: "" });
  const [error, setError] = useState("");
  const [sending, setSending] = useState(false);
  const [view, setView] = useState(() =>
    new URLSearchParams(window.location.search).get("reset_token") ? "reset" : "login"
  );
  const [resetToken] = useState(() => new URLSearchParams(window.location.search).get("reset_token") || "");
  const [showPw, setShowPw] = useState(false);
  const [forgotEmail, setForgotEmail] = useState("");
  const [forgotDone, setForgotDone] = useState(false);
  const [resetForm, setResetForm] = useState({ pw: "", pw2: "" });
  const [showResetPw, setShowResetPw] = useState(false);
  const [accountOpen, setAccountOpen] = useState(false);
  const [accountBusy, setAccountBusy] = useState(false);
  const [pwForm, setPwForm] = useState({ current: "", pw1: "", pw2: "" });
  const [showAccountPw, setShowAccountPw] = useState(false);
  const [emailForm, setEmailForm] = useState({ email: "", password: "" });

  useEffect(() => {
    (async () => {
      try {
        const { data } = await client.get(`${API}/auth/me`);
        setUser(data);
      } catch {
        setUser(false);
      }
    })();
  }, []);

  const perms = user?.permissions || [];
  const visibleTabs = user ? TABS.filter((t) => perms.includes(t.perm)) : [];

  useEffect(() => {
    if (!user) return;
    if (!tab || !visibleTabs.some((t) => t.id === tab)) {
      setTab(visibleTabs[0]?.id || null);
    }
  }, [user, visibleTabs, tab]);

  const login = async (e) => {
    e.preventDefault();
    setSending(true);
    setError("");
    try {
      const { data } = await client.post(`${API}/auth/login`, form);
      setUser(data);
      toast.success(`Welcome back, ${data.name || "admin"}`);
    } catch (err) {
      if (!err.response) {
        setError("Cannot reach the server — it may be restarting. Wait 20 seconds and try again.");
      } else {
        setError(formatDetail(err.response?.data?.detail));
      }
    } finally {
      setSending(false);
    }
  };

  const forgotSubmit = async (e) => {
    e.preventDefault();
    setSending(true);
    setError("");
    try {
      await client.post(`${API}/auth/forgot-password`, { email: forgotEmail });
    } catch {}
    setForgotDone(true);
    setSending(false);
  };

  const resetSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (resetForm.pw !== resetForm.pw2) {
      setError("Passwords do not match");
      return;
    }
    setSending(true);
    try {
      await client.post(`${API}/auth/reset-password`, { token: resetToken, new_password: resetForm.pw });
      toast.success("Password updated — sign in with your new password");
      window.history.replaceState({}, "", "/admin");
      setForm({ email: "", password: "" });
      setView("login");
    } catch (err) {
      setError(formatDetail(err.response?.data?.detail));
    } finally {
      setSending(false);
    }
  };

  const logout = async () => {
    try {
      await client.post(`${API}/auth/logout`);
    } catch {}
    setUser(false);
    setTab(null);
  };

  const changePassword = async (e) => {
    e.preventDefault();
    if (pwForm.pw1 !== pwForm.pw2) {
      toast.error("New passwords do not match");
      return;
    }
    setAccountBusy(true);
    try {
      const { data } = await client.post(`${API}/auth/change-password`, { current_password: pwForm.current, new_password: pwForm.pw1 });
      toast.success(data.message);
      setPwForm({ current: "", pw1: "", pw2: "" });
    } catch (err) {
      toast.error(formatDetail(err.response?.data?.detail, "Could not change password"));
    } finally {
      setAccountBusy(false);
    }
  };

  const changeEmail = async (e) => {
    e.preventDefault();
    setAccountBusy(true);
    try {
      const { data } = await client.post(`${API}/auth/change-email`, { new_email: emailForm.email, password: emailForm.password });
      toast.success(data.message);
      setAccountOpen(false);
      setUser(false);
      setTab(null);
      setForm({ email: emailForm.email, password: "" });
    } catch (err) {
      toast.error(formatDetail(err.response?.data?.detail, "Could not change email"));
    } finally {
      setAccountBusy(false);
    }
  };

  const active = TABS.find((t) => t.id === tab);
  const isAdmin = user?.role === "admin";

  return (
    <div data-testid="admin-page" className="min-h-screen bg-ink text-white relative flex flex-col">
      <div className="absolute inset-0 texture-perf opacity-40 pointer-events-none" />

      <header className="relative bg-royal border-b border-white/10">
        <div className="max-w-[1440px] mx-auto px-5 md:px-10 h-16 md:h-[72px] flex items-center justify-between">
          <div className="flex items-center gap-3">
            <span className="bg-white px-2.5 py-1">
              <img src="/assets/dv-logo.png" alt="D V Steels logo" className="h-5 w-auto" />
            </span>
            <span className="leading-tight">
              <span className="block font-heading font-bold uppercase tracking-wider text-white text-base">D V Steels</span>
              <span className="block font-mono text-[9px] tracking-[0.25em] uppercase text-white/60">Cable Tray · Quotation Portal</span>
            </span>
          </div>
          <div className="flex items-center gap-3">
            <a
              data-testid="back-to-site-link"
              href="/"
              className="hidden sm:flex items-center gap-2 border border-white/20 px-3 md:px-4 py-2 text-white/80 hover:bg-white hover:text-ink transition-colors duration-300"
            >
              <ArrowLeft size={14} />
              <span className="font-heading font-semibold uppercase tracking-wider text-sm">Back to Website</span>
            </a>
            {user && (
              <>
                <button
                  data-testid="account-settings-button"
                  onClick={() => setAccountOpen(true)}
                  title="Change email / password"
                  className="hidden md:block text-right leading-tight px-3 py-1.5 border border-white/20 hover:bg-white/10 transition-colors duration-300"
                >
                  <span className="block font-mono text-[9px] tracking-[0.25em] uppercase text-white/60">{isAdmin ? "Admin" : "Sales"}</span>
                  <span className="block font-heading font-semibold uppercase tracking-wider text-sm text-white">{user.name}</span>
                </button>
                <button
                  data-testid="admin-logout-button"
                  onClick={logout}
                  className="btn-sweep btn-ghost font-heading font-semibold uppercase tracking-wider text-sm px-4 py-2 flex items-center gap-2"
                >
                  <LogOut size={15} /> Logout
                </button>
              </>
            )}
          </div>
        </div>
        {user && visibleTabs.length > 0 && (
          <nav className="max-w-[1440px] mx-auto px-5 md:px-10 flex gap-1 overflow-x-auto" data-testid="admin-tabbar">
            {visibleTabs.map((t) => (
              <button
                key={t.id}
                data-testid={`tab-${t.id}`}
                onClick={() => setTab(t.id)}
                className={`flex items-center gap-2 font-heading font-semibold uppercase tracking-wider text-xs px-4 py-3 border-b-2 whitespace-nowrap transition-colors duration-300 ${
                  tab === t.id ? "border-[#7FB3E8] text-white bg-white/5" : "border-transparent text-white/60 hover:text-white"
                }`}
              >
                <t.icon size={14} strokeWidth={1.8} />
                {t.label}
              </button>
            ))}
          </nav>
        )}
      </header>

      <main className="relative flex-1 max-w-[1440px] w-full mx-auto px-5 md:px-10 py-10 md:py-14">
        {user === null && (
          <div className="flex justify-center py-24" data-testid="admin-loading">
            <Loader2 size={36} className="animate-spin text-smoke" />
          </div>
        )}

        {user === false && (
          <div className="max-w-md mx-auto">
            <p className="font-mono text-[10px] tracking-[0.35em] uppercase text-smoke mb-3">Restricted</p>
            <h1 className="font-heading font-semibold uppercase text-3xl md:text-4xl tracking-wide text-white mb-8">
              {view === "login" && "Admin Login"}
              {view === "forgot" && "Reset Password"}
              {view === "reset" && "New Password"}
            </h1>
            <div className="bg-white text-ink p-7 md:p-9 shadow-[0_24px_60px_rgba(0,0,0,0.4)]">
              {view === "login" && (
                <form data-testid="admin-login-form" onSubmit={login} className="flex flex-col gap-6">
                  <div>
                    <label htmlFor="admin-email" className="font-mono text-[10px] tracking-[0.3em] uppercase text-royal">Email</label>
                    <input
                      id="admin-email"
                      data-testid="admin-email-input"
                      type="email"
                      required
                      value={form.email}
                      onChange={(e) => setForm({ ...form, email: e.target.value })}
                      placeholder="admin email"
                      className="input-line"
                    />
                  </div>
                  <div>
                    <label htmlFor="admin-password" className="font-mono text-[10px] tracking-[0.3em] uppercase text-royal">Password</label>
                    <div className="relative">
                      <input
                        id="admin-password"
                        data-testid="admin-password-input"
                        type={showPw ? "text" : "password"}
                        required
                        value={form.password}
                        onChange={(e) => setForm({ ...form, password: e.target.value })}
                        placeholder="password"
                        className="input-line pr-10"
                      />
                      <button
                        type="button"
                        data-testid="admin-password-eye"
                        onClick={() => setShowPw(!showPw)}
                        aria-label={showPw ? "Hide password" : "Show password"}
                        className="absolute right-0 top-1/2 -translate-y-1/2 p-1 text-ink/40 hover:text-royal transition-colors duration-300"
                      >
                        {showPw ? <EyeOff size={18} /> : <Eye size={18} />}
                      </button>
                    </div>
                  </div>
                  {error && (
                    <p data-testid="admin-login-error" className="font-mono text-xs tracking-widest uppercase text-red-700">{error}</p>
                  )}
                  <button
                    data-testid="admin-login-button"
                    type="submit"
                    disabled={sending}
                    className="btn-sweep btn-navy font-heading font-semibold uppercase tracking-wider text-lg px-8 py-3 flex items-center justify-center gap-2 disabled:opacity-60"
                  >
                    {sending ? <Loader2 size={20} className="animate-spin" /> : <>Sign In <Lock size={17} /></>}
                  </button>
                  <button
                    type="button"
                    data-testid="forgot-password-link"
                    onClick={() => { setError(""); setForgotDone(false); setView("forgot"); }}
                    className="w-fit mx-auto font-mono text-[10px] tracking-[0.25em] uppercase text-ink/50 hover:text-royal transition-colors duration-300"
                  >
                    Forgot password?
                  </button>
                </form>
              )}

              {view === "forgot" && (
                <form data-testid="forgot-password-form" onSubmit={forgotSubmit} className="flex flex-col gap-6">
                  <p className="font-body text-sm text-ink/70 leading-relaxed">
                    Enter your admin email — a password reset link will be sent to it.
                  </p>
                  <div>
                    <label htmlFor="forgot-email" className="font-mono text-[10px] tracking-[0.3em] uppercase text-royal">Email</label>
                    <input
                      id="forgot-email"
                      data-testid="forgot-email-input"
                      type="email"
                      required
                      value={forgotEmail}
                      onChange={(e) => setForgotEmail(e.target.value)}
                      placeholder="admin email"
                      className="input-line"
                    />
                  </div>
                  {forgotDone && (
                    <p data-testid="forgot-success" className="font-mono text-xs tracking-widest uppercase text-royal">
                      If this email is registered, a reset link is on its way.
                    </p>
                  )}
                  <button
                    data-testid="forgot-submit-button"
                    type="submit"
                    disabled={sending}
                    className="btn-sweep btn-navy font-heading font-semibold uppercase tracking-wider text-lg px-8 py-3 flex items-center justify-center gap-2 disabled:opacity-60"
                  >
                    {sending ? <Loader2 size={20} className="animate-spin" /> : "Send Reset Link"}
                  </button>
                  <button
                    type="button"
                    data-testid="back-to-login-button"
                    onClick={() => { setError(""); setView("login"); }}
                    className="w-fit mx-auto font-mono text-[10px] tracking-[0.25em] uppercase text-ink/50 hover:text-royal transition-colors duration-300"
                  >
                    ← Back to login
                  </button>
                </form>
              )}

              {view === "reset" && (
                <form data-testid="reset-password-form" onSubmit={resetSubmit} className="flex flex-col gap-6">
                  <div>
                    <label htmlFor="reset-password" className="font-mono text-[10px] tracking-[0.3em] uppercase text-royal">New Password</label>
                    <div className="relative">
                      <input
                        id="reset-password"
                        data-testid="reset-password-input"
                        type={showResetPw ? "text" : "password"}
                        required
                        minLength={8}
                        value={resetForm.pw}
                        onChange={(e) => setResetForm({ ...resetForm, pw: e.target.value })}
                        placeholder="min. 8 characters"
                        className="input-line pr-10"
                      />
                      <button
                        type="button"
                        data-testid="reset-password-eye"
                        onClick={() => setShowResetPw(!showResetPw)}
                        aria-label={showResetPw ? "Hide password" : "Show password"}
                        className="absolute right-0 top-1/2 -translate-y-1/2 p-1 text-ink/40 hover:text-royal transition-colors duration-300"
                      >
                        {showResetPw ? <EyeOff size={18} /> : <Eye size={18} />}
                      </button>
                    </div>
                  </div>
                  <div>
                    <label htmlFor="reset-password-confirm" className="font-mono text-[10px] tracking-[0.3em] uppercase text-royal">Confirm Password</label>
                    <input
                      id="reset-password-confirm"
                      data-testid="reset-password-confirm-input"
                      type={showResetPw ? "text" : "password"}
                      required
                      minLength={8}
                      value={resetForm.pw2}
                      onChange={(e) => setResetForm({ ...resetForm, pw2: e.target.value })}
                      placeholder="repeat new password"
                      className="input-line"
                    />
                  </div>
                  {error && (
                    <p data-testid="reset-password-error" className="font-mono text-xs tracking-widest uppercase text-red-700">{error}</p>
                  )}
                  <button
                    data-testid="reset-submit-button"
                    type="submit"
                    disabled={sending}
                    className="btn-sweep btn-navy font-heading font-semibold uppercase tracking-wider text-lg px-8 py-3 flex items-center justify-center gap-2 disabled:opacity-60"
                  >
                    {sending ? <Loader2 size={20} className="animate-spin" /> : "Set New Password"}
                  </button>
                </form>
              )}
            </div>
          </div>
        )}

        {user && active && (
          <div data-testid="admin-dashboard">
            <div className="mb-8">
              <p className="font-mono text-[10px] tracking-[0.35em] uppercase text-smoke mb-2">{active.eyebrow}</p>
              <h1 className="font-heading font-semibold uppercase text-3xl md:text-4xl tracking-wide text-white">{active.title}</h1>
              <p className="mt-2 font-body text-sm text-smoke">{active.sub}</p>
            </div>

            {tab === "dash" && <AdminDashboard client={client} api={API} goTab={setTab} isAdmin={isAdmin} />}
            {tab === "customers" && <AdminCustomers client={client} api={API} perms={perms} />}
            {tab === "new-quote" && <AdminQuoteNew client={client} api={API} goTab={setTab} userName={user.name} />}
            {tab === "quotes" && <AdminQuotations client={client} api={API} perms={perms} />}
            {tab === "orders" && <AdminOrders client={client} api={API} goTab={setTab} perms={perms} />}
            {tab === "billing" && <AdminBilling client={client} api={API} perms={perms} />}
            {tab === "statements" && <AdminStatements client={client} api={API} />}
            {tab === "ledger" && <AdminLedger client={client} api={API} />}
            {tab === "calculator" && <AdminCalculator client={client} api={API} isAdmin={isAdmin} />}
            {tab === "enquiries" && <AdminEnquiries client={client} api={API} />}
            {tab === "rates" && <AdminRates client={client} api={API} canEdit={perms.includes("rates:edit")} />}
            {tab === "team" && <TeamSetup client={client} api={API} />}
          </div>
        )}

        {user && accountOpen && (
          <div className="fixed inset-0 z-50 bg-ink/90 overflow-y-auto p-4 md:p-10" data-testid="account-modal">
            <div className="max-w-lg mx-auto bg-steel border border-white/10 p-6 md:p-8 relative">
              <button data-testid="account-close" onClick={() => setAccountOpen(false)} aria-label="Close" className="absolute top-4 right-4 p-2 text-white/50 hover:text-white">
                <X size={20} />
              </button>
              <p className="font-mono text-[10px] tracking-[0.35em] uppercase text-smoke mb-1">Account</p>
              <h2 className="font-heading font-semibold uppercase text-2xl tracking-wide text-white mb-2">Login Settings</h2>
              <p className="font-body text-xs text-smoke mb-6">Signed in as <span className="text-white">{user.email}</span></p>

              <form data-testid="change-password-form" onSubmit={changePassword} className="border border-white/10 p-5 mb-5 flex flex-col gap-4">
                <p className="font-heading font-semibold uppercase tracking-wider text-sm text-white">Change Password</p>
                <div>
                  <label className="font-mono text-[10px] tracking-[0.3em] uppercase text-smoke">Current Password</label>
                  <div className="relative">
                    <input
                      data-testid="current-password-input"
                      type={showAccountPw ? "text" : "password"}
                      required
                      value={pwForm.current}
                      onChange={(e) => setPwForm({ ...pwForm, current: e.target.value })}
                      className="mt-1 w-full bg-ink border border-white/20 px-4 py-2.5 pr-10 font-body text-sm text-white focus:border-[#7FB3E8] focus:outline-none"
                    />
                    <button type="button" data-testid="account-password-eye" onClick={() => setShowAccountPw(!showAccountPw)} aria-label={showAccountPw ? "Hide passwords" : "Show passwords"} className="absolute right-2 top-1/2 -translate-y-1/2 mt-0.5 p-1 text-white/40 hover:text-white">
                      {showAccountPw ? <EyeOff size={16} /> : <Eye size={16} />}
                    </button>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="font-mono text-[10px] tracking-[0.3em] uppercase text-smoke">New Password</label>
                    <input
                      data-testid="new-password-input"
                      type={showAccountPw ? "text" : "password"}
                      required
                      minLength={8}
                      value={pwForm.pw1}
                      onChange={(e) => setPwForm({ ...pwForm, pw1: e.target.value })}
                      placeholder="min. 8 characters"
                      className="mt-1 w-full bg-ink border border-white/20 px-4 py-2.5 font-body text-sm text-white placeholder:text-white/30 focus:border-[#7FB3E8] focus:outline-none"
                    />
                  </div>
                  <div>
                    <label className="font-mono text-[10px] tracking-[0.3em] uppercase text-smoke">Confirm New</label>
                    <input
                      data-testid="confirm-password-input"
                      type={showAccountPw ? "text" : "password"}
                      required
                      minLength={8}
                      value={pwForm.pw2}
                      onChange={(e) => setPwForm({ ...pwForm, pw2: e.target.value })}
                      className="mt-1 w-full bg-ink border border-white/20 px-4 py-2.5 font-body text-sm text-white focus:border-[#7FB3E8] focus:outline-none"
                    />
                  </div>
                </div>
                <button data-testid="change-password-button" type="submit" disabled={accountBusy} className="btn-sweep btn-navy font-heading font-semibold uppercase tracking-wider text-sm px-5 py-2.5 flex items-center justify-center gap-2 disabled:opacity-60">
                  {accountBusy ? <Loader2 size={15} className="animate-spin" /> : "Update Password"}
                </button>
              </form>

              <form data-testid="change-email-form" onSubmit={changeEmail} className="border border-white/10 p-5 flex flex-col gap-4">
                <p className="font-heading font-semibold uppercase tracking-wider text-sm text-white">Change Login Email</p>
                <div>
                  <label className="font-mono text-[10px] tracking-[0.3em] uppercase text-smoke">New Email</label>
                  <input
                    data-testid="new-email-input"
                    type="email"
                    required
                    value={emailForm.email}
                    onChange={(e) => setEmailForm({ ...emailForm, email: e.target.value })}
                    className="mt-1 w-full bg-ink border border-white/20 px-4 py-2.5 font-body text-sm text-white focus:border-[#7FB3E8] focus:outline-none"
                  />
                </div>
                <div>
                  <label className="font-mono text-[10px] tracking-[0.3em] uppercase text-smoke">Confirm With Password</label>
                  <input
                    data-testid="email-password-input"
                    type="password"
                    required
                    value={emailForm.password}
                    onChange={(e) => setEmailForm({ ...emailForm, password: e.target.value })}
                    className="mt-1 w-full bg-ink border border-white/20 px-4 py-2.5 font-body text-sm text-white focus:border-[#7FB3E8] focus:outline-none"
                  />
                </div>
                <p className="font-body text-xs text-yellow-300/80">You will be signed out and must log in again with the new email.</p>
                <button data-testid="change-email-button" type="submit" disabled={accountBusy} className="btn-sweep btn-ghost font-heading font-semibold uppercase tracking-wider text-sm px-5 py-2.5 flex items-center justify-center gap-2 disabled:opacity-60">
                  {accountBusy ? <Loader2 size={15} className="animate-spin" /> : "Update Email"}
                </button>
              </form>
            </div>
          </div>
        )}
      </main>

      <footer className="relative py-8 text-center">
        <p className="font-mono text-[10px] tracking-[0.35em] uppercase text-white/40">D V Steels · Strong. Reliable. Built to Last.</p>
      </footer>
    </div>
  );
}
